import { MenuOutlined } from "@ant-design/icons";
import { Layout, Divider, Button } from "antd";
import { Header, Content } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSecureService } from "../store/service";
import { useAtom } from "jotai";
import { userAtom } from "../store/atoms";
import styles from "./mainLayout.module.scss";
import AuthChecker from "../components/AuthChecker/AuthChecker";
import SidebarContent from "../components/Sidebar/SidebarContent";
import UserSection from "../components/UserSection/UserSection";
import MobileDrawer from "../components/MobileDrawer/MobileDrawer";

const getSavedSidebarCollapsed = (fallback: boolean) => {
  try {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      return JSON.parse(saved);
    }
  } catch {
    // ignore
  }
  return fallback;
};

const MainLayout = ({
  children,
  title,
  icon,
  headerActions,
  autoCollapse,
  forceInitialCollapse
}: {
  children: React.ReactNode,
  title: string,
  icon: React.ReactNode,
  headerActions?: React.ReactNode,
  autoCollapse?: boolean,
  forceInitialCollapse?: boolean
}) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const { doLogout } = useSecureService();
  const navigate = useNavigate();
  const [user] = useAtom(userAtom);

  const location = useLocation();

  // Mobile drawer state
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Sidebar collapse state - forceInitialCollapse varsa başlangıçta kapalı yap, yoksa localStorage'dan al veya default değeri kullan
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (forceInitialCollapse) {
      return true;
    }
    return getSavedSidebarCollapsed(autoCollapse || false);
  });



  // Sidebar collapse handler
  const handleSidebarCollapse = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  };

  // forceInitialCollapse değiştiğinde sadece başlangıçta sidebar'ı kapalı yap
  useLayoutEffect(() => {
    if (forceInitialCollapse) {
      setIsSidebarCollapsed(true);
      localStorage.setItem('sidebarCollapsed', JSON.stringify(true));
    }
  }, [forceInitialCollapse]);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle('sidebar-collapsed', isSidebarCollapsed);
  }, [isSidebarCollapsed, location.pathname]);

  // Login olmamış kullanıcılar ve Guest kullanıcılar için korumalı sayfalar kontrolü
  useEffect(() => {
    const protectedPaths = ['/dashboard', '/settings'];
    const currentPath = location.pathname;
    
    if ((!user || user.role === 'Guest') && protectedPaths.includes(currentPath)) {
      navigate('/internal-projects');
    }
    
    // Ana sayfa kontrolü
    if (currentPath === '/') {
      if (!user || user.role === 'Guest') {
        navigate('/internal-projects');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, location.pathname, navigate]);

  // Menu click handler
  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      doLogout();
      navigate('/login');
    } else if (key === 'login') {
      navigate('/login');
    } else {
      setSelectedKeys([key]);
      
      // Mobile drawer'ı kapat
      if (isMobile) {
        setIsMobileDrawerOpen(false);
      }
      
      navigate(key);
    }
  };

  // Logout handler
  const handleLogout = async () => {
    await doLogout();
    navigate('/login');
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/internal-projects') || currentPath.startsWith('/projects') || currentPath.startsWith('/snps')) {
      setSelectedKeys(['/internal-projects']);
    } else if (currentPath.startsWith('/external-projects')) {
      setSelectedKeys(['/external-projects']);
    } else {
      setSelectedKeys([currentPath]);
    }
  }, [location.pathname]);



  return (
    <AuthChecker
      isMobile={isMobile}
      selectedKeys={selectedKeys}
      onMenuClick={handleMenuClick}
      onMobileDrawerOpen={() => setIsMobileDrawerOpen(true)}
    >
      <Layout className={styles.layout}>
        {/* Desktop Sider */}
        {!isMobile && (
          <Sider 
            width={270} 
            collapsedWidth={60}
            defaultCollapsed={isSidebarCollapsed}
            collapsed={isSidebarCollapsed}
            className={styles.sider}
          >
            <SidebarContent 
              selectedKeys={selectedKeys}
              onMenuClick={({ key }) => {
                if (key === 'logout') {
                  doLogout();
                  navigate('/login');
                } else if (key === 'login') {
                  navigate('/login');
                } else {
                  navigate(key);
                }
              }}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => handleSidebarCollapse(!isSidebarCollapsed)}
            />
          </Sider>
        )}
        
        <Layout className={isSidebarCollapsed ? styles.layoutCollapsed : ''}>
          <Header className={styles.header}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <Button 
                type="primary" 
                icon={<MenuOutlined />} 
                onClick={() => setIsMobileDrawerOpen(true)}
                className={styles.mobileMenuButton}
              />
            )}
            
            <div className={styles.userContainer}>
              <UserSection 
                isMobile={isMobile}
                onMobileDrawerClose={() => setIsMobileDrawerOpen(false)}
              />
            </div>
          </Header>
          <Content className={styles.content}>
            <div className={styles.contentContainer}>
              <div className={styles.contentHeader}>
                <div className={styles.contentHeaderTop}>
                  <h1>{icon} {title}</h1>
                  {headerActions ? (
                    <div className={styles.contentHeaderActions}>{headerActions}</div>
                  ) : null}
                </div>
                <Divider type="horizontal" className={styles.divider} />
              </div>
              <div className={styles.contentBody}>
                {children}
              </div>
            </div>
          </Content>
        </Layout>
        
        {/* Mobile Drawer */}
        <MobileDrawer 
          isOpen={isMobileDrawerOpen}
          onClose={() => setIsMobileDrawerOpen(false)}
          selectedKeys={selectedKeys}
          onMenuClick={handleMenuClick}
        />
      </Layout>
    </AuthChecker>
  );
};

export default MainLayout;
